import React, { useMemo } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { H2, Button, Line, Flex, P } from '../../lib/components'
import type {
  InterestPayoutPreviewResponse,
  InterestPayoutResponse,
  InterestPayoutLine,
} from '../../services/transaction-service'
import type {
  ConfirmPayoutLineRow,
  EligibleInvestmentOption,
  OpportunityOption,
} from './interest-payout-form-types'
import './interest-payout-form.scss'

interface InterestPayoutFormPresenterProps {
  opportunities: OpportunityOption[]
  opportunitiesLoading: boolean
  selectedOpportunityIds: Set<number>
  onToggleOpportunity: (id: number) => void
  onSelectAllOpportunities: () => void
  eligibleInvestments: EligibleInvestmentOption[]
  investmentsLoading: boolean
  selectedInvestmentIds: Set<number>
  onToggleInvestment: (id: number) => void
  onSelectAllInvestments: () => void
  selectionStats: { investmentCount: number; investorCount: number }
  onReview: (dates: {
    payout_date?: string
    period_start_date: string
    period_end_date: string
  }) => void
  confirmOpen: boolean
  previewData: InterestPayoutPreviewResponse | null
  previewLoading: boolean
  previewError: string | null
  confirmRows: ConfirmPayoutLineRow[]
  reinvestSelection: Set<number>
  onToggleReinvest: (investmentId: number) => void
  onToggleReinvestAll: () => void
  onCloseConfirm: () => void
  onConfirmExecute: () => void
  isSubmitting: boolean
  result: InterestPayoutResponse | null
  resultError: string | null
}

const toISODateString = (d: Date): string => d.toISOString().split('T')[0]

const firstDayOfMonth = (d: Date): string =>
  toISODateString(new Date(d.getFullYear(), d.getMonth(), 1))

const formatIsoDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

const formatDollars = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const centsToDollars = (cents: number) => Math.round((cents / 100) * 100) / 100

const statusLabel = (s: InterestPayoutLine['status']): string => {
  switch (s) {
    case 'processed':
      return 'Payout'
    case 'reinvested':
      return 'Reinvest'
    case 'skipped_duplicate':
      return 'Skipped (duplicate)'
    case 'skipped_zero_interest':
      return 'Skipped (zero interest)'
    case 'skipped_raise_goal':
      return 'Skipped (raise goal)'
    default:
      return s
  }
}

const summarizeLines = (lines: InterestPayoutLine[] | undefined) => {
  if (!lines?.length) return null
  const counts = lines.reduce((acc, line) => {
    acc[line.status] = (acc[line.status] ?? 0) + 1
    return acc
  }, {} as Record<string, number>)
  return counts
}

const InterestPayoutFormPresenter = ({
  opportunities,
  opportunitiesLoading,
  selectedOpportunityIds,
  onToggleOpportunity,
  onSelectAllOpportunities,
  eligibleInvestments,
  investmentsLoading,
  selectedInvestmentIds,
  onToggleInvestment,
  onSelectAllInvestments,
  selectionStats,
  onReview,
  confirmOpen,
  previewData,
  previewLoading,
  previewError,
  confirmRows,
  reinvestSelection,
  onToggleReinvest,
  onToggleReinvestAll,
  onCloseConfirm,
  onConfirmExecute,
  isSubmitting,
  result,
  resultError,
}: InterestPayoutFormPresenterProps) => {
  const today = useMemo(() => toISODateString(new Date()), [])
  const defaultPeriodStart = useMemo(() => firstDayOfMonth(new Date()), [])
  const [payoutDate, setPayoutDate] = React.useState<string>(today)
  const [periodStartDate, setPeriodStartDate] = React.useState<string>(defaultPeriodStart)
  const [periodEndDate, setPeriodEndDate] = React.useState<string>(today)

  const filteredOpportunities = useMemo(
    () => opportunities.filter((o) => o.opportunity_title),
    [opportunities],
  )

  const handleReviewClick = () => {
    onReview({
      payout_date: payoutDate || undefined,
      period_start_date: periodStartDate,
      period_end_date: periodEndDate,
    })
  }

  const canReview =
    selectedOpportunityIds.size > 0 &&
    !previewLoading &&
    !isSubmitting &&
    !!periodStartDate &&
    !!periodEndDate

  const actionableRows = useMemo(() => confirmRows.filter((row) => row.actionable), [confirmRows])
  const skippedRows = useMemo(() => confirmRows.filter((row) => !row.actionable), [confirmRows])
  const allReinvestSelected =
    actionableRows.length > 0 &&
    actionableRows.every((row) => reinvestSelection.has(row.investment_id))

  const liveEstimates = useMemo(() => {
    let payoutCents = 0
    let reinvestCents = 0
    let payoutCount = 0
    let reinvestCount = 0
    for (const row of actionableRows) {
      if (reinvestSelection.has(row.investment_id)) {
        reinvestCents += row.interest_amount_cents
        reinvestCount += 1
      } else {
        payoutCents += row.interest_amount_cents
        payoutCount += 1
      }
    }
    return {
      payoutCount,
      reinvestCount,
      payoutDollars: centsToDollars(payoutCents),
      reinvestDollars: centsToDollars(reinvestCents),
    }
  }, [actionableRows, reinvestSelection])

  return (
    <Flex variant='column-center' classes={['interest-payout-form-container']}>
      <H2 classes={['mb-48']} text='Interest Payout' />

      <div className='instructions-container' data-testid='interest-payout-instructions'>
        <P classes={['mb-16']} text='Process interest payouts for Fixed Income opportunities:' />
        <ul>
          <li className='instruction-item'>
            Only opportunities marked as Fixed Income (in Add/Edit Opportunity) are listed.
          </li>
          <li className='instruction-item'>
            Select one or more opportunities and set the interest period (start and end date).
          </li>
          <li className='instruction-item'>
            Optionally narrow to specific investments; leave none selected to include all eligible
            investments for the chosen opportunities.
          </li>
          <li className='instruction-item'>
            Only investments with status TRADE_EXECUTED and a unit allocation date are included.
            Fully withdrawn lots (remaining quantity 0) that were held during the interest period
            remain eligible so mid-cycle accrued interest is still paid.
          </li>
          <li className='instruction-item'>
            Duplicate payouts are skipped only for the same investment row (same investment / order)
            on the selected payout date—multiple investments per investor in one opportunity are
            each paid separately.
          </li>
          <li className='instruction-item'>
            Review the preview, then select or deselect reinvestment per client before confirming.
          </li>
          <li className='instruction-item'>
            Example: $10,000 at 12% annual rate for 30 days ≈ $98.63 interest.
          </li>
        </ul>
      </div>

      <Line />

      {opportunitiesLoading ? (
        <Flex variant='column-center' classes={['mt-24']}>
          <CircularProgress size={32} />
          <P classes={['mt-16']} text='Loading opportunities...' />
        </Flex>
      ) : (
        <>
          <P classes={['mt-16']} text='Select opportunities' />
          <div className='opportunity-list' data-testid='interest-payout-opportunity-list'>
            {filteredOpportunities.length > 0 && (
              <div className='select-all-wrap'>
                <Button
                  variant='secondary-btn'
                  text={
                    selectedOpportunityIds.size === filteredOpportunities.length
                      ? 'Deselect all'
                      : 'Select all'
                  }
                  handleClick={onSelectAllOpportunities}
                  dataTestId='interest-payout-select-all'
                  classes={['select-all-btn']}
                />
              </div>
            )}
            {filteredOpportunities.length === 0 ? (
              <P
                text='No Fixed Income opportunities available. Mark opportunities as Fixed Income in the Add/Edit Opportunity form to enable interest payouts here.'
                classes={['empty-state-text']}
              />
            ) : (
              filteredOpportunities.map((opp) => (
                <div key={opp.id} className='opportunity-checkbox-item'>
                  <label className='opportunity-checkbox-label'>
                    <input
                      type='checkbox'
                      checked={selectedOpportunityIds.has(opp.id)}
                      onChange={() => onToggleOpportunity(opp.id)}
                      data-testid={`interest-payout-opportunity-${opp.id}`}
                      className='opportunity-checkbox-input'
                    />
                    <span>
                      {opp.opportunity_title} (ID: {opp.id})
                    </span>
                  </label>
                </div>
              ))
            )}
          </div>

          {selectedOpportunityIds.size > 0 && (
            <>
              <P classes={['mt-24']} text='Investments (optional filter)' />
              {investmentsLoading ? (
                <Flex variant='column-center' classes={['mt-8']}>
                  <CircularProgress size={24} />
                  <P classes={['mt-8']} text='Loading investments...' />
                </Flex>
              ) : eligibleInvestments.length === 0 ? (
                <P
                  text='No eligible investments found for the selected opportunities.'
                  classes={['empty-state-text']}
                />
              ) : (
                <>
                  <div className='select-all-wrap'>
                    <Button
                      variant='secondary-btn'
                      text={
                        selectedInvestmentIds.size === eligibleInvestments.length
                          ? 'Clear investment selection'
                          : 'Select all investments'
                      }
                      handleClick={onSelectAllInvestments}
                      dataTestId='interest-payout-select-all-investments'
                      classes={['select-all-btn']}
                    />
                  </div>
                  <P
                    classes={['mb-8', 'instruction-item']}
                    text='Leave all unchecked to pay all eligible investors for the selected opportunities.'
                  />
                  <div
                    className='mb-8 interest-payout-selection-stats'
                    data-testid='interest-payout-selection-stats'
                  >
                    <P
                      text={`In scope: ${selectionStats.investmentCount} investment(s), ${selectionStats.investorCount} investor(s)`}
                    />
                  </div>
                  <div
                    className='opportunity-list interest-payout-investment-list'
                    data-testid='interest-payout-investment-list'
                  >
                    {eligibleInvestments.map((inv) => (
                      <div key={inv.id} className='opportunity-checkbox-item'>
                        <label className='opportunity-checkbox-label'>
                          <input
                            type='checkbox'
                            checked={selectedInvestmentIds.has(inv.id)}
                            onChange={() => onToggleInvestment(inv.id)}
                            data-testid={`interest-payout-investment-${inv.id}`}
                            className='opportunity-checkbox-input'
                          />
                          <span>
                            {inv.opportunity_title}: {inv.label}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <P classes={['mt-16']} text='Dates (YYYY-MM-DD)' />
          <div className='date-fields'>
            <div className='date-field-group'>
              <label htmlFor='payout-date'>Payout date</label>
              <input
                id='payout-date'
                type='date'
                value={payoutDate}
                onChange={(e) => setPayoutDate(e.target.value)}
                data-testid='interest-payout-date'
              />
            </div>
            <div className='date-field-group'>
              <label htmlFor='period-start'>Period start</label>
              <input
                id='period-start'
                type='date'
                value={periodStartDate}
                onChange={(e) => setPeriodStartDate(e.target.value)}
                data-testid='interest-payout-period-start'
                required
              />
            </div>
            <div className='date-field-group'>
              <label htmlFor='period-end'>Period end</label>
              <input
                id='period-end'
                type='date'
                value={periodEndDate}
                onChange={(e) => setPeriodEndDate(e.target.value)}
                data-testid='interest-payout-period-end'
                required
              />
            </div>
          </div>

          <div className='submit-section'>
            <Button
              variant='primary-btn'
              text='Review & confirm'
              isDisabled={!canReview}
              handleClick={handleReviewClick}
              dataTestId='interest-payout-review'
              classes={['mt-16', 'p-16']}
            />
            {(previewLoading || isSubmitting) && (
              <span className='submit-spinner'>
                <CircularProgress size={20} />
              </span>
            )}
          </div>
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={onCloseConfirm}
        maxWidth='md'
        fullWidth
        classes={{ paper: 'interest-payout-confirm-dialog' }}
      >
        <DialogTitle className='interest-payout-confirm-title'>Confirm interest payout</DialogTitle>
        <DialogContent className='interest-payout-confirm-content'>
          {previewError && <P classes={['mb-8']} text={previewError} />}
          {previewData && (
            <div
              className='interest-payout-confirm-body'
              data-testid='interest-payout-confirm-summary'
            >
              <div className='interest-payout-confirm-meta'>
                <P text={`Payout date: ${formatIsoDate(previewData.payout_date)}`} />
                <P
                  text={`Period: ${formatIsoDate(previewData.period_start_date)} → ${formatIsoDate(
                    previewData.period_end_date,
                  )}`}
                />
              </div>

              <div className='interest-payout-summary-cards'>
                <div className='interest-payout-summary-card'>
                  <span className='interest-payout-summary-label'>Investments</span>
                  <strong>{previewData.total_investments}</strong>
                </div>
                <div className='interest-payout-summary-card'>
                  <span className='interest-payout-summary-label'>Investors</span>
                  <strong>{previewData.total_investors}</strong>
                </div>
                <div className='interest-payout-summary-card'>
                  <span className='interest-payout-summary-label'>Payouts (est.)</span>
                  <strong>
                    {liveEstimates.payoutCount} · ${formatDollars(liveEstimates.payoutDollars)}
                  </strong>
                </div>
                <div className='interest-payout-summary-card'>
                  <span className='interest-payout-summary-label'>Reinvest (est.)</span>
                  <strong>
                    {liveEstimates.reinvestCount} · ${formatDollars(liveEstimates.reinvestDollars)}
                  </strong>
                </div>
              </div>

              {actionableRows.length > 0 && (
                <div className='interest-payout-reinvest-section'>
                  <div className='interest-payout-reinvest-header'>
                    <div>
                      <P text='Reinvestment selection' />
                      <P
                        classes={['instruction-item']}
                        text={`${reinvestSelection.size} of ${actionableRows.length} set to reinvest`}
                      />
                    </div>
                    <Button
                      variant='secondary-btn'
                      text={allReinvestSelected ? 'Deselect all' : 'Select all'}
                      handleClick={onToggleReinvestAll}
                      dataTestId='interest-payout-reinvest-select-all'
                      classes={['select-all-btn']}
                    />
                  </div>
                  <div
                    className='interest-payout-client-list'
                    data-testid='interest-payout-reinvest-list'
                  >
                    {actionableRows.map((row) => (
                      <label
                        key={row.investment_id}
                        className='interest-payout-client-row'
                        data-testid={`interest-payout-reinvest-row-${row.investment_id}`}
                      >
                        <input
                          type='checkbox'
                          checked={reinvestSelection.has(row.investment_id)}
                          onChange={() => onToggleReinvest(row.investment_id)}
                          className='opportunity-checkbox-input'
                          data-testid={`interest-payout-reinvest-${row.investment_id}`}
                        />
                        <div className='interest-payout-client-row-main'>
                          <span className='interest-payout-client-name'>{row.label}</span>
                          <span className='interest-payout-client-meta'>
                            {row.opportunity_title}
                          </span>
                        </div>
                        <div className='interest-payout-client-row-side'>
                          <span className='interest-payout-client-amount'>
                            ${formatDollars(centsToDollars(row.interest_amount_cents))}
                          </span>
                          <span
                            className={`interest-payout-status-chip ${
                              reinvestSelection.has(row.investment_id)
                                ? 'interest-payout-status-chip--reinvest'
                                : 'interest-payout-status-chip--payout'
                            }`}
                          >
                            {reinvestSelection.has(row.investment_id) ? 'Reinvest' : 'Payout'}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {skippedRows.length > 0 && (
                <div className='interest-payout-skipped-section'>
                  <P text='Skipped lines' />
                  <div className='interest-payout-client-list interest-payout-client-list--skipped'>
                    {skippedRows.map((row) => (
                      <div
                        key={`skip-${row.investment_id}-${row.status}`}
                        className='interest-payout-client-row interest-payout-client-row--disabled'
                      >
                        <div className='interest-payout-client-row-main'>
                          <span className='interest-payout-client-name'>{row.label}</span>
                          <span className='interest-payout-client-meta'>
                            {row.opportunity_title}
                          </span>
                        </div>
                        <span className='interest-payout-status-chip interest-payout-status-chip--skipped'>
                          {statusLabel(row.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(previewData.skipped_duplicate > 0 ||
                previewData.skipped_zero_interest > 0 ||
                (previewData.skipped_raise_goal ?? 0) > 0) && (
                <div className='interest-payout-skip-counts'>
                  <P text='Skip counts from preview:' />
                  <P text={` · Duplicate: ${previewData.skipped_duplicate}`} />
                  <P text={` · Zero interest: ${previewData.skipped_zero_interest}`} />
                  <P text={` · Raise goal: ${previewData.skipped_raise_goal ?? 0}`} />
                </div>
              )}

              <P
                classes={['mt-16']}
                text='Estimates update with your selection. Confirm to create INTEREST_PAYOUT / INTEREST_REINVESTED transactions.'
              />
            </div>
          )}
        </DialogContent>
        <DialogActions className='interest-payout-dialog-actions'>
          <Button
            variant='secondary-btn'
            text='Cancel'
            handleClick={onCloseConfirm}
            dataTestId='interest-payout-cancel'
          />
          <Button
            variant='primary-btn'
            text='Confirm & process'
            handleClick={() => void onConfirmExecute()}
            isDisabled={!previewData || isSubmitting}
            dataTestId='interest-payout-confirm'
          />
        </DialogActions>
      </Dialog>

      {result && (
        <div className='result-summary' data-testid='interest-payout-result-success' role='status'>
          <P classes={['mb-8']} text='Payout processed successfully.' />
          <div className='result-details'>
            <P text={`Opportunities processed: ${result.total_opportunities_processed}`} />
            <P text={`Payout transactions created: ${result.total_transactions_created}`} />
            <P text={`Total payout amount: $${result.total_payout_amount.toLocaleString()}`} />
            <P text={`Reinvestments created: ${result.total_reinvestments_created ?? 0}`} />
            <P
              text={`Total reinvested amount: $${(
                result.total_reinvested_amount ?? 0
              ).toLocaleString()}`}
            />
            {result.results.length > 0 && (
              <div className='result-opportunity-item'>
                <P classes={['mt-8']} text='Per opportunity:' />
                {result.results.map((r) => {
                  const summary = summarizeLines(r.payout_lines ?? [])
                  return (
                    <div key={r.opportunity_id} className='result-opportunity-item'>
                      • {r.opportunity_title}: {r.transactions_created} payout txn(s), $
                      {r.total_payout_amount.toLocaleString()}; {r.reinvestments_created ?? 0}{' '}
                      reinvestment(s), ${(r.total_reinvested_amount ?? 0).toLocaleString()}
                      {summary && (
                        <div className='payout-status-breakdown'>
                          {Object.entries(summary).map(([status, count]) => (
                            <span key={status} className='payout-status-chip'>
                              {statusLabel(status as InterestPayoutLine['status'])}: {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {resultError && (
        <div
          className='result-summary result-summary-error'
          data-testid='interest-payout-result-error'
          role='alert'
        >
          <P text={resultError} />
        </div>
      )}
    </Flex>
  )
}

export { InterestPayoutFormPresenter }
